import { FormCommonProps } from "src/components/common/forms/types.ts";
import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import {
	InvoiceLineEditApi,
	InvoiceLineEditApi_FormInitData,
} from "src/api/generated/erp/sales/invoicing/api/invoiceLineEditApi.ts";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import i18n from "i18next";
import { DeepPartial } from "react-hook-form";
import {
	ObjectAccountingForm,
	ObjectAccountingFormValues,
} from "src/components/views/erp/common/accounting/ObjectAccountingForm.tsx";
import {
	getObjectAccountingCodeIdsFromFormValues,
	getObjectAccountingFormDefaultValues,
} from "src/components/views/erp/common/accounting/ObjectAccountingForm.utils.ts";
import { IsoDateTimeString } from "../../../../../types/dateTime.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface InvoiceLineFormProps extends FormCommonProps<number> {
	invoiceLineId: number;
}

interface FormValues extends ObjectAccountingFormValues {
	salesPartNo: string;
	linePartDescription: string;
	mark: string;
	unitPrice: number;
	discountPercent: number;
	quantityInSalesUnits: number;
	lastUpdated: IsoDateTimeString;
}

export const InvoiceLineForm = ({ invoiceLineId, onCompleted, onFormEdited }: InvoiceLineFormProps) => {
	return (
		<AsyncForm<InvoiceLineEditApi_FormInitData, FormValues, number>
			fetch={() => InvoiceLineEditApi.getFormInitData({ invoiceLineId })}
			getDefaultValues={getDefaultValues}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			submit={submit}
			columns={2}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.invoiceLine.form"),
				title: i18n.t("invoice_line"),
				pathSegments: [i18n.t("form")],
				data: { invoiceLineId },
			}}
			render={(params) => <FormContent {...params} />}
		/>
	);

	function getDefaultValues({
		invoiceLine,
		accountingDimensions,
	}: InvoiceLineEditApi_FormInitData): DeepPartial<FormValues> {
		const unitPrice = invoiceLine.priceInputWithVat ? invoiceLine.unitPriceWithVat : invoiceLine.unitPrice;
		return {
			salesPartNo: invoiceLine.salesPartNo,
			linePartDescription: invoiceLine.linePartDescription,
			mark: invoiceLine.mark,
			unitPrice: unitPrice,
			discountPercent: invoiceLine.discountPercent,
			quantityInSalesUnits: invoiceLine.quantityInSalesUnits,
			lastUpdated: invoiceLine.lastUpdated,
			...getObjectAccountingFormDefaultValues(accountingDimensions),
		};
	}

	async function submit(values: FormValues): Promise<number> {
		const accountingCodeIds = getObjectAccountingCodeIdsFromFormValues(values.accountingDimensionValues);
		await InvoiceLineEditApi.update({
			request: {
				invoiceLineId: invoiceLineId,
				salesPartNo: values.salesPartNo,
				linePartDescription: values.linePartDescription,
				mark: values.mark,
				unitPrice: values.unitPrice,
				discountPercent: values.discountPercent,
				quantityInSalesUnits: values.quantityInSalesUnits,
				lastUpdated: values.lastUpdated,
			},
			accountingCodeIds: accountingCodeIds,
		});
		return invoiceLineId;
	}
};

interface FormContentProps extends AsyncFormContentParams<InvoiceLineEditApi_FormInitData, FormValues> {}

const FormContent = ({ control, data }: FormContentProps) => {
	const labelVatPart = data.invoiceLine.priceInputWithVat ? i18n.t("with_vat") : i18n.t("without_vat");
	const labelSalesUnitPart =
		data.invoiceLine.salesUnit != null && data.invoiceLine.salesUnit !== "" ?
			` (${data.invoiceLine.salesUnit})`
		:	"";
	const unitPriceLabel = `${i18n.t("unit_price")}${labelSalesUnitPart}, ${labelVatPart}`;
	const quantityLabel =
		data.invoiceLine.salesUnit != null && data.invoiceLine.salesUnit !== "" ?
			`${i18n.t("quantity")} (${data.invoiceLine.salesUnit})`
		:	i18n.t("quantity");

	return (
		<>
			<FormTextField control={control} name={"salesPartNo"} label={i18n.t("part_no")} spanGridColumns />
			<FormTextField
				control={control}
				name={"linePartDescription"}
				label={i18n.t("sales_part")}
				spanGridColumns
			/>
			<FormTextField control={control} name={"mark"} label={i18n.t("mark")} spanGridColumns />
			<FormNumberField control={control} name={"unitPrice"} label={unitPriceLabel} spanGridColumns />
			<FormNumberField control={control} name={"quantityInSalesUnits"} label={quantityLabel} />
			<FormNumberField control={control} name={"discountPercent"} label={i18n.t("discount_percent")} />
			<ObjectAccountingForm control={control} />
		</>
	);
};
